import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import type { Documentary, FilterOptions, SortOption, TVChannel, Category, ApiDocumentary } from '@/types'
import { generateMockRatingBreakdown, calculateTotalRatings } from '@/utils/ratingCalculator'
import { documentaryAPI } from '@/services/api'
import { debugError, debugLog } from '@/utils/env'
import { useChannelStore } from './channel'

// 根据频道推断分类
const getCategoryFromChannel = (channel: string): Category => {
  const channelCategoryMap: Record<string, Category> = {
    'bbc': 'nature',
    'nhk': 'culture',
    'cctv': 'culture',
    'discovery': 'nature',
    'NG': 'science',
    'hbo': 'society',
    'netflix': 'nature',
    'amazon': 'science',
    'apple': 'technology',
    'youtube': 'all'
  }
  return channelCategoryMap[channel] || 'all'
}

// 动态获取频道 ID（从 channelStore）
let channelStore: ReturnType<typeof useChannelStore> | null = null
const getChannelStore = () => {
  if (!channelStore) {
    channelStore = useChannelStore()
  }
  return channelStore
}

// 频道 code 到 ID 的映射（从 channelStore 获取）
const getChannelIdByCode = (code: string): number | null => {
  const store = getChannelStore()
  return store.channelCodeToId[code] || null
}

// 频道 ID 到 code 的映射（从 channelStore 获取）
const getChannelCodeById = (id: number): TVChannel => {
  const store = getChannelStore()
  return store.channelIdToCode[id] || 'all'
}

// 模拟纪录片数据
const generateMockDocumentaries = (): Documentary[] => {
  const templates = [
    {
      title: "地球脉动",
      tvChannel: 'bbc' as TVChannel,
      category: 'nature' as Category,
      director: "大卫·爱登堡",
      description: "BBC出品的经典自然纪录片系列，展现了地球上各种生物的生存状态和自然环境的壮美。",
      tags: ["自然", "野生动物", "4K", "环保"],
      duration: "60分钟/集",
      episodes: 8,
      rating: 9.8,
      year: 2016,
      country: "英国",
      genre: ["自然", "科学"]
    },
    {
      title: "蓝色星球",
      tvChannel: 'bbc' as TVChannel,
      category: 'nature' as Category,
      director: "詹姆斯·霍尼伯内",
      description: "深入探索海洋世界的奇迹，展现海洋生物的多样性和海洋生态系统的复杂性。",
      tags: ["海洋", "生态", "环保", "水下摄影"],
      duration: "60分钟/集",
      episodes: 8,
      rating: 9.7,
      year: 2017,
      country: "英国",
      genre: ["自然", "科学"]
    },
    {
      title: "舌尖上的中国",
      tvChannel: 'cctv' as TVChannel,
      category: 'food' as Category,
      director: "陈晓卿",
      description: "探索中国各地的美食文化，展现传统美食的制作工艺和背后的文化故事。",
      tags: ["美食", "文化", "传统", "中国"],
      duration: "50分钟/集",
      episodes: 8,
      rating: 9.4,
      year: 2012,
      country: "中国",
      genre: ["文化", "美食"]
    },
    {
      title: "我们的星球",
      tvChannel: 'netflix' as TVChannel,
      category: 'nature' as Category,
      director: "阿拉斯泰尔·福瑟吉尔",
      description: "Netflix投资制作的大型自然纪录片，展现地球生态系统的脆弱性。",
      tags: ["环保", "生态", "自然", "气候变化"],
      duration: "50分钟/集",
      episodes: 8,
      rating: 9.3,
      year: 2019,
      country: "美国",
      genre: ["自然", "环保"]
    },
    {
      title: "宇宙时空之旅",
      tvChannel: 'NG' as TVChannel,
      category: 'science' as Category,
      director: "安·德鲁扬",
      description: "探索宇宙的奥秘，从大爆炸到现代天文学的最新发现。",
      tags: ["宇宙", "科学", "天文", "探索"],
      duration: "45分钟/集",
      episodes: 13,
      rating: 9.6,
      year: 2014,
      country: "美国",
      genre: ["科学", "天文"]
    },
    {
      title: "日本之美",
      tvChannel: 'nhk' as TVChannel,
      category: 'culture' as Category,
      director: "田中诚",
      description: "NHK精心制作的日本自然风光纪录片，展现日本四季变换的美景。",
      tags: ["自然", "日本", "四季", "风景"],
      duration: "45分钟/集",
      episodes: 4,
      rating: 9.3,
      year: 2018,
      country: "日本",
      genre: ["自然", "文化"]
    },
    {
      title: "野生动物大救援",
      tvChannel: 'discovery' as TVChannel,
      category: 'nature' as Category,
      director: "迈克尔·冈瑟",
      description: "记录全球野生动物保护工作者的努力和野生动物保护的重要性。",
      tags: ["保护", "野生动物", "环保", "救援"],
      duration: "50分钟/集",
      episodes: 6,
      rating: 9.0,
      year: 2020,
      country: "美国",
      genre: ["自然", "环保"]
    },
    {
      title: "徒手攀岩",
      tvChannel: 'netflix' as TVChannel,
      category: 'biography' as Category,
      director: "伊丽莎白·柴·瓦萨赫伊",
      description: "记录攀岩大师亚历克斯·霍诺德无保护徒手攀登酋长岩的壮举。",
      tags: ["运动", "冒险", "励志", "人物传记"],
      duration: "100分钟",
      episodes: 1,
      rating: 9.1,
      year: 2018,
      country: "美国",
      genre: ["运动", "传记"]
    }
  ]

  // 生成更多数据
  const documentaries: Documentary[] = []
  templates.forEach((template, index) => {
    const ratingBreakdown = generateMockRatingBreakdown(template.rating)
    documentaries.push({
      id: `doc-${index + 1}`,
      ...template,
      ratingBreakdown,
      totalRatings: calculateTotalRatings(ratingBreakdown)
    })
  })

  // 添加一些变体数据
  for (let i = 9; i <= 125; i++) {
    const baseTemplate = templates[i % templates.length]
    const rating = Math.max(7.5, baseTemplate.rating - Math.random() * 2)
    const ratingBreakdown = generateMockRatingBreakdown(rating)
    
    documentaries.push({
      id: `doc-${i}`,
      title: `${baseTemplate.title} ${i > templates.length ? `第${Math.ceil(i / templates.length)}部` : ''}`,
      tvChannel: baseTemplate.tvChannel,
      category: baseTemplate.category,
      director: baseTemplate.director,
      description: baseTemplate.description,
      tags: baseTemplate.tags,
      duration: baseTemplate.duration,
      episodes: baseTemplate.episodes,
      rating,
      ratingBreakdown,
      totalRatings: calculateTotalRatings(ratingBreakdown),
      year: baseTemplate.year + Math.floor(Math.random() * 5),
      country: baseTemplate.country,
      genre: baseTemplate.genre
    })
  }

  return documentaries
}

export const useDocumentaryStore = defineStore('documentary', () => {
  const documentaries = ref<Documentary[]>([])
  const loading = ref(false)
  const error = ref<string | null>(null)
  const currentPage = ref(1)
  const itemsPerPage = ref(24)
  const totalItems = ref(0)
  const totalPages = ref(0)
  const hasMore = ref(true)
  const maxPages = ref(50) // 最多加载50页
  const filters = ref<FilterOptions>({
    tvChannel: 'all',
    category: 'all',
    year: undefined,
    director: '',
    tags: [],
    minRating: undefined,
    maxRating: undefined,
    genre: []
  })
  const sortBy = ref<SortOption['value']>('date')
  const searchQuery = ref('')

  // 从API获取纪录片列表
  const fetchDocumentaries = async (reset: boolean = false) => {
    // 如果是重置，先保存原有数据用于错误时恢复
    const previousDocumentaries = reset ? [...documentaries.value] : documentaries.value
    
    try {
      loading.value = true
      error.value = null

      // 确保频道数据已加载（用于 ID <-> Code 映射）
      await getChannelStore().fetchChannels()

      // 重置时清空数据，加载时保留
      if (reset) {
        documentaries.value = []
        currentPage.value = 1
      }

      const page = reset ? 1 : currentPage.value
      
      // 检查是否超过最大页数
      if (page > maxPages.value) {
        hasMore.value = false
        loading.value = false
        return
      }

      const params: any = {
        page,
        limit: itemsPerPage.value
      }

      // 添加过滤条件 - 同时使用 channelid 和 channel 参数确保兼容性
      if (filters.value.tvChannel !== 'all') {
        // 根据频道 code 获取频道 ID
        const channelId = getChannelIdByCode(filters.value.tvChannel)
        if (channelId) {
          params.channelid = channelId
        }
        // 同时添加 channel 参数作为后备（后端支持通过 channel code 查找）
        params.channel = filters.value.tvChannel
      }
      if (filters.value.category !== 'all') {
        params.category = filters.value.category
      }
      if (searchQuery.value) {
        params.search = searchQuery.value
      }
      if (sortBy.value) {
        params.sortBy = sortBy.value
      }
      if (filters.value.year) {
        params.year = filters.value.year
      }
      if (filters.value.director) {
        params.director = filters.value.director
      }
      if (filters.value.minRating !== undefined) {
        params.minRating = filters.value.minRating
      }
      if (filters.value.maxRating !== undefined) {
        params.maxRating = filters.value.maxRating
      }
      if (filters.value.tags && filters.value.tags.length > 0) {
        params.tags = filters.value.tags
      }
      if (filters.value.genre && filters.value.genre.length > 0) {
        params.genre = filters.value.genre
      }

      const response: any = await documentaryAPI.getDocumentaries(params)
      
      // 转换API数据格式为前端期望格式
      const apiDocumentaries = response.data?.documentaries || []
      
      const convertedDocumentaries = apiDocumentaries.map((doc: ApiDocumentary) => {
        // 生成评分分解数据
        const ratingBreakdown = generateMockRatingBreakdown(doc.rating || 8.5)

        // 从 TVChannelID 反推频道 code
        const tvChannelCode = getChannelCodeById(doc.tv_channel_id ?? 0)

        // 使用 slug 作为前端 id（如果存在），否则使用 documentary_id
        const docId = (doc as any).slug || doc.documentary_id.toString()

        return {
          id: docId,
          title: doc.title,
          tvChannel: tvChannelCode, // 使用反推的频道 code
          category: getCategoryFromChannel(tvChannelCode), // 根据频道推断分类
          director: doc.director,
          description: doc.description,
          coverImage: doc.cover,
          tags: doc.tags || [],
          duration: doc.duration || '未知',
          episodes: doc.episodes || 1,
          rating: doc.rating || 8.5,
          ratingBreakdown,
          totalRatings: calculateTotalRatings(ratingBreakdown),
          year: doc.year,
          country: doc.country || '未知',
          genre: doc.genre || [],
          videoURLs: doc.video_urls || []  // 新增：视频地址列表
        }
      })
      
      if (reset) {
        documentaries.value = convertedDocumentaries
      } else {
        // loadMore 时追加数据，保留原有数据
        documentaries.value.push(...convertedDocumentaries)
      }
      
      totalItems.value = response.data?.total || 0
      totalPages.value = Math.ceil(totalItems.value / itemsPerPage.value)
      // 限制最多加载 maxPages 页
      hasMore.value = currentPage.value < totalPages.value && currentPage.value < maxPages.value

    } catch (err) {
      debugError('获取纪录片列表失败:', err)
      error.value = err instanceof Error ? err.message : '获取数据失败'
      
      // 错误时恢复原有数据，不清空
      if (reset) {
        // 如果有之前的数据，恢复它
        if (previousDocumentaries.length > 0) {
          documentaries.value = previousDocumentaries
        } else {
          // 首次加载失败，使用模拟数据作为降级方案
          documentaries.value = generateMockDocumentaries()
          totalItems.value = documentaries.value.length
          totalPages.value = Math.ceil(totalItems.value / itemsPerPage.value)
        }
      }
      // loadMore 失败时不改变原有数据
    } finally {
      loading.value = false
    }
  }

  // 从API获取单个纪录片详情（支持 slug 或 id）
  const fetchDocumentaryById = async (identifier: string | number): Promise<Documentary | null> => {
    try {
      const response: any = await documentaryAPI.getDocumentary(identifier)
      const doc: ApiDocumentary = response.data?.documentary
      if (!doc) return null

      // 使用后端返回的真实评分分布数据
      const backendBreakdown = response.data?.ratingBreakdown
      const backendTotalRatings = response.data?.totalRatings ?? doc.total_ratings ?? 0

      // 如果后端没有返回评分分布，使用模拟数据
      const ratingBreakdown = backendBreakdown ? {
        zeroStars: backendBreakdown.zeroStars || 0,
        oneStars: backendBreakdown.oneStars || 0,
        twoStars: backendBreakdown.twoStars || 0,
        threeStars: backendBreakdown.threeStars || 0,
        fourStars: backendBreakdown.fourStars || 0,
        fiveStars: backendBreakdown.fiveStars || 0
      } : generateMockRatingBreakdown(doc.rating || 8.5)

      // 使用 slug 作为前端 id（如果存在），否则使用 documentary_id
      const docId = (doc as any).slug || doc.documentary_id.toString()

      return {
        id: docId,
        title: doc.title,
        tvChannel: (doc.channel || 'all') as TVChannel,
        category: getCategoryFromChannel(doc.channel || 'all'),
        director: doc.director,
        description: doc.description,
        coverImage: doc.cover,
        tags: doc.tags || [],
        duration: doc.duration ? `${doc.duration}分钟` : '未知',
        episodes: doc.episodes || 1,
        rating: doc.rating || 8.5,
        ratingBreakdown,
        totalRatings: backendTotalRatings || calculateTotalRatings(ratingBreakdown),
        year: doc.year,
        country: doc.country || '未知',
        genre: doc.genre || [],
        videoURLs: doc.video_urls || []
      }
    } catch (err) {
      debugError('获取纪录片详情失败:', err)
      // 从本地数据中查找
      return getDocumentaryById(String(identifier)) ?? null
    }
  }

  // 初始化数据
  const initializeData = async () => {
    if (documentaries.value.length === 0) {
      debugLog('Fetching documentary data from API...')
      await fetchDocumentaries(true)
      debugLog('Documentaries loaded:', documentaries.value.length)
    }
  }

  // 确保数据已初始化
  const ensureData = async () => {
    if (documentaries.value.length === 0) {
      await initializeData()
    }
  }

  // 计算属性
  const filteredDocumentaries = computed(() => {
    let filtered = [...documentaries.value]


    if (searchQuery.value) {
      const query = searchQuery.value.toLowerCase()
      filtered = filtered.filter(doc => 
        doc.title.toLowerCase().includes(query) ||
        doc.director.toLowerCase().includes(query) ||
        doc.description.toLowerCase().includes(query) ||
        doc.tags.some(tag => tag.toLowerCase().includes(query))
      )
    }

    // 电视频道过滤
    if (filters.value.tvChannel !== 'all') {
      filtered = filtered.filter(doc => doc.tvChannel === filters.value.tvChannel)
    }

    // 分类过滤
    if (filters.value.category !== 'all') {
      filtered = filtered.filter(doc => doc.category === filters.value.category)
    }

    // 导演过滤
    if (filters.value.director) {
      const director = filters.value.director.toLowerCase()
      filtered = filtered.filter(doc => 
        doc.director.toLowerCase().includes(director)
      )
    }

    // 年份过滤
    if (filters.value.year) {
      filtered = filtered.filter(doc => {
        const docYear = doc.year || 2020
        return docYear === filters.value.year
      })
    }

    // 评分过滤
    if (filters.value.minRating !== undefined) {
      filtered = filtered.filter(doc => doc.rating >= filters.value.minRating!)
    }

    if (filters.value.maxRating !== undefined) {
      filtered = filtered.filter(doc => doc.rating <= filters.value.maxRating!)
    }

    // 标签过滤
    if (filters.value.tags && filters.value.tags.length > 0) {
      filtered = filtered.filter(doc =>
        filters.value.tags!.some(tag => doc.tags.includes(tag))
      )
    }

    // 排序
    filtered.sort((a, b) => {
      switch (sortBy.value) {
        case 'rating':
          return b.rating - a.rating
        case 'name':
          return a.title.localeCompare(b.title, 'zh-CN')
        case 'year':
          return (b.year || 0) - (a.year || 0)
        case 'date':
        default:
          return (b.year || 0) - (a.year || 0)
      }
    })

    return filtered
  })

  const paginatedDocumentaries = computed(() => {
    if (totalItems.value > 0) {
      // API分页数据
      return documentaries.value
    } else {
      // 本地模拟数据分页
      const start = (currentPage.value - 1) * itemsPerPage.value
      const end = start + itemsPerPage.value
      return filteredDocumentaries.value.slice(start, end)
    }
  })

  // 方法
  const loadMore = async () => {
    if (hasMore.value && currentPage.value < maxPages.value) {
      currentPage.value++
      await fetchDocumentaries(false)
    }
  }

  const resetPagination = () => {
    currentPage.value = 1
  }

  // 清空纪录片列表
  const clearDocumentaries = () => {
    documentaries.value = []
    totalItems.value = 0
  }

  const setFilters = async (newFilters: Partial<FilterOptions>) => {
    filters.value = { ...filters.value, ...newFilters }
    resetPagination()
    await fetchDocumentaries(true)
  }

  // 专门用于频道页面的筛选器设置，重置其他筛选项
  const setChannelFilter = async (channel: TVChannel) => {
    filters.value = {
      tvChannel: channel,
      category: 'all',
      year: undefined,
      director: '',
      tags: [],
      minRating: undefined,
      maxRating: undefined,
      genre: []
    }
    searchQuery.value = ''
    resetPagination()
    await fetchDocumentaries(true)
  }

  const setSortBy = async (sort: SortOption['value']) => {
    sortBy.value = sort
    resetPagination()
    await fetchDocumentaries(true)
  }

  const setSearchQuery = async (query: string) => {
    searchQuery.value = query
    resetPagination()
    await fetchDocumentaries(true)
  }

  // 根据 id 或 slug 查找纪录片
  const getDocumentaryById = (id: string): Documentary | undefined => {
    // 优先匹配 id
    const byId = documentaries.value.find(doc => doc.id === id)
    if (byId) return byId
    
    // 兼容旧格式的 id（如 doc-1）
    return documentaries.value.find(doc => doc.id === id || doc.id === `doc-${id}`)
  }

  const getDocumentariesByChannel = (channel: TVChannel): Documentary[] => {
    // 使用filteredDocumentaries获取已过滤的数据（包含分页前的全部筛选结果）
    return filteredDocumentaries.value.filter(doc => doc.tvChannel === channel)
  }

  const getAllTags = (): string[] => {
    const allTags = new Set<string>()
    documentaries.value.forEach(doc => {
      doc.tags.forEach(tag => allTags.add(tag))
    })
    return Array.from(allTags).sort()
  }

  const getAllDirectors = (): string[] => {
    const allDirectors = new Set<string>()
    documentaries.value.forEach(doc => {
      allDirectors.add(doc.director)
    })
    return Array.from(allDirectors).sort()
  }

  // 刷新数据
  const refresh = async () => {
    resetPagination()
    await fetchDocumentaries(true)
  }

  // 不在这里立即初始化，让 Home.vue 的 onMounted 触发
  // 这样可以确保在 PWA 首次打开时正确加载数据

  return {
    // 状态
    documentaries,
    loading,
    error,
    currentPage,
    itemsPerPage,
    totalItems,
    totalPages,
    hasMore,
    filters,
    sortBy,
    searchQuery,
    
    // 计算属性
    filteredDocumentaries,
    paginatedDocumentaries,
    
    // 方法
    loadMore,
    resetPagination,
    setFilters,
    setChannelFilter,
    setSortBy,
    setSearchQuery,
    getDocumentaryById,
    getDocumentariesByChannel,
    ensureData,
    initializeData,
    fetchDocumentaries,
    fetchDocumentaryById,
    refresh,
    clearDocumentaries,
    getAllTags,
    getAllDirectors
  }


})